"use client";

import { useEffect, useRef, ReactNode } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import Header from "../components/Header";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
}

const AnimatedSection = ({
  children,
  className = "",
}: AnimatedSectionProps) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeIn}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-grow">
        {/* Hero Section with Gradient Text */}
        <div className="w-full h-[250px] flex items-center justify-center bg-gradient-to-b from-gray-100 to-white">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-[100px] mt-10 font-bold text-transparent bg-clip-text uppercase tracking-wider"
            style={{
              backgroundImage:
                "linear-gradient(180deg, #000000 0%, #DBDBDB 72.42%, rgba(219, 219, 219, 0.00) 100%)",
            }}
          >
            ABOUT US
          </motion.h1>
        </div>

        {/* Our Story Section */}
        <section className="py-16 px-4 container mx-auto max-w-6xl">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-4xl font-bold uppercase text-[#1C1C1C] mb-6">
              Our Story
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Sri Garage was founded with a simple mission: to make finding and
              purchasing vehicle spare parts easier for Sri Lankans. What
              started as a small operation has grown into the country&apos;s
              premier online marketplace for automotive parts.
            </p>
          </AnimatedSection>
        </section>

        {/* What We Offer Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto max-w-6xl">
            <AnimatedSection className="text-center mb-16">
              <h2 className="text-4xl font-bold uppercase text-[#1C1C1C] mb-6">
                What We Offer
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Sri Garage is more than just a marketplace. We&apos;re a
                complete solution for all your vehicle spare part needs.
              </p>
            </AnimatedSection>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {[
                {
                  title: "Extensive Catalog",
                  description:
                    "Access thousands of genuine and aftermarket parts for all major vehicle brands in Sri Lanka.",
                  icon: "🔍",
                },
                {
                  title: "Vehicle-Specific Search",
                  description:
                    "Find parts that are guaranteed to fit your specific make, model, and year.",
                  icon: "🚗",
                },
                {
                  title: "Fast Delivery",
                  description:
                    "Get your parts delivered to your doorstep within 24 hours in major cities.",
                  icon: "🚚",
                },
                {
                  title: "Expert Support",
                  description:
                    "Our team of automotive experts is available to help you find the right parts.",
                  icon: "👨‍🔧",
                },
                {
                  title: "Quality Guarantee",
                  description:
                    "All parts sold on our platform meet strict quality standards and come with a warranty.",
                  icon: "✅",
                },
                {
                  title: "Competitive Pricing",
                  description:
                    "We work directly with suppliers to offer you the best prices in the market.",
                  icon: "💰",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="text-xl font-bold text-[#1C1C1C] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 px-4 bg-[#1C1C1C] text-white">
          <div className="container mx-auto max-w-6xl">
            <AnimatedSection className="text-center">
              <h2 className="text-4xl font-bold uppercase mb-6">
                Ready to Find Your Parts?
              </h2>
              <p className="text-lg max-w-3xl mx-auto mb-8">
                Browse our extensive catalog of vehicle spare parts and find
                exactly what you need for your vehicle.
              </p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a
                  href="/shop"
                  className="inline-block bg-red-600 text-white font-bold uppercase py-4 px-8 rounded-full hover:bg-red-700 transition-colors"
                >
                  Shop Now
                </a>
              </motion.div>
            </AnimatedSection>
          </div>
        </section>
      </main>
    </div>
  );
}
